import React, { useState, useMemo, useEffect } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
} from "reactstrap"
import { FaPlus, FaPen, FaSearch, FaEye } from "react-icons/fa"
import { FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa"
import Swal from "sweetalert2"
import moment from "moment"

import ProductModal from "./ProductModal"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { Switch } from "antd"

const Product = () => {
  const [products, setProducts] = useState([])
  const [productData, setProductData] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState("") // "add" | "edit" | "view"

  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [loading, setLoading] = useState(false)

  const getAllProducts = async () => {
    try {
      const response = await fetchData(setLoading, "product/catalog") // fixed: removed setLoading as arg if not expected
      if (response?.success) {
        setProducts(response.data.reverse() || [])
      } else {
        Swal.fire({
          title: "Error",
          text: response?.message || "Failed to load products",
          icon: "error",
        })
      }
    } catch (err) {
      console.error(err)
      Swal.fire({
        title: "Error",
        text: "Network or server error",
        icon: "error",
      })
    }
  }

  useEffect(() => {
    getAllProducts()
  }, [])

  const filteredProducts = useMemo(() => {
    if (!searchString.trim()) return products
    const lowerSearch = searchString.toLowerCase()
    return products.filter(
      p =>
        p.productName?.toLowerCase().includes(lowerSearch) ||
        p.price?.toLowerCase().includes(lowerSearch) ||
        p.supplier.supplierName?.toLowerCase().includes(lowerSearch) ||
        p.measuringUnit?.toLowerCase().includes(lowerSearch)
    )
  }, [products, searchString])

  const totalItems = filteredProducts.length
  const totalPages = Math.ceil(totalItems / perPageItem)

  const indexOfLastItem = currentPage * perPageItem
  const indexOfFirstItem = indexOfLastItem - perPageItem
  const currentProducts = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchString, perPageItem])

  const openModal = (type = "", data = {}) => {
    setModalType(type)
    setProductData(data)
    setIsModalOpen(true)
  }

  const toggleModal = () => setIsModalOpen(prev => !prev)

  const handleSave = async updatedData => {
    try {
      let response
      if (updatedData.id) {
        response = await FetchPutData(
          setLoading,
          "product/catalog",
          updatedData.id,
          updatedData
        )
        if (response?.data?.success) {
          Swal.fire("Success", "Product updated", "success")
        }
      } else {
        response = await FetchPostData(
          setLoading,
          "product/catalog",
          updatedData
        )
        if (response?.data) {
          Swal.fire("Success", "Product created", "success")
        }
      }

      if (response?.data?.success || response?.data) {
        getAllProducts()
        toggleModal()
      } else {
        throw new Error(response?.response?.data?.message || "Operation failed")
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Something went wrong",
        icon: "error",
      })
    }
  }

  const toggleStatus = async (id, newIsActive) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Change product status to ${newIsActive ? "Active" : "Inactive"}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3d6eb6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    })

    if (!result.isConfirmed) return

    // Optimistic update
    const previousProducts = [...products]
    setProducts(prev =>
      prev.map(p => (p.id === id ? { ...p, status: newIsActive } : p))
    )
    try {
      const response = await FetchPutData(setLoading, "product/catalog", id, {
        status: newIsActive, // ← change to { status: newIsActive } if your backend uses "status"
      })

      if (response?.data?.success || response?.data) {
        Swal.fire({
          title: "Success",
          text: "Status updated",
          icon: "success",
          timer: 1500,
        })
        // Optional: getAllProducts(); // if you prefer re-fetch over optimistic
      } else {
        setProducts(previousProducts) // revert
        throw new Error(response?.response?.data?.message || "Update failed")
      }
    } catch (err) {
      setProducts(previousProducts) // revert on error
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to update status",
        icon: "error",
      })
    }
  }

  const toggleChargable = async (id, newIsActive) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Change product to ${newIsActive ? "Chargable" : "Non Chargable"}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3d6eb6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    })

    if (!result.isConfirmed) return

    // Optimistic update
    const previousProducts = [...products]
    setProducts(prev =>
      prev.map(p => (p.id === id ? { ...p, isChargeable: newIsActive } : p))
    )
    try {
      const response = await FetchPutData(setLoading, "product/catalog", id, {
        isChargeable: newIsActive, // ← change to { status: newIsActive } if your backend uses "status"
      })

      if (response?.data?.success || response?.data) {
        Swal.fire({
          title: "Success",
          text: "Status updated",
          icon: "success",
          timer: 1500,
        })
        // Optional: getAllProducts(); // if you prefer re-fetch over optimistic
      } else {
        setProducts(previousProducts) // revert
        throw new Error(response?.response?.data?.message || "Update failed")
      }
    } catch (err) {
      setProducts(previousProducts) // revert on error
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to update status",
        icon: "error",
      })
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        {loading && <Loader />}

        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Products"
          description="Manage your products catalog"
        />

        <Row>
          <Col lg="12" className="px-0">
            <Card className="mb-0 shadow-lg">
              <CardBody>
                {/* Controls */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="position-relative">
                      <input
                        className="form-control rounded-4 ps-3 pe-5"
                        placeholder="Search by product name..."
                        value={searchString}
                        onChange={e => setSearchString(e.target.value)}
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                    </div>
                    <select
                      className="form-select rounded-4 w-auto"
                      value={perPageItem}
                      onChange={e => setPerPageItem(Number(e.target.value))}
                    >
                      <option value={10}>Show 10</option>
                      <option value={20}>Show 20</option>
                      <option value={50}>Show 50</option>
                    </select>
                  </div>

                  <Button
                    color="primary"
                    className="btn-blue"
                    onClick={() => openModal("add", {})}
                  >
                    <FaPlus className="me-2" /> Add Product
                  </Button>
                </div>

                {/* Table */}
                <Table responsive hover className="align-middle">
                  <thead className="bg-blue text-white">
                    <tr>
                      <th>#</th>
                      <th>Product Name</th>
                      <th className="text-center">Measuring Unit</th>
                      <th className="text-center">Total Price (£)</th>
                      <th className="text-center">Supplier</th>
                      <th className="text-center">Stock Status</th>
                      <th className="text-center">Chargable</th>
                      <th className="text-center">Added At</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-4">
                          No products found
                        </td>
                      </tr>
                    ) : (
                      currentProducts.map((product, idx) => (
                        <tr key={product.id}>
                          <td>{indexOfFirstItem + idx + 1}</td>
                          <td>{product.productName || "—"}</td>
                          <td className="text-center">
                            {product.measuringUnit || "—"}
                          </td>
                          <td className="text-center">
                            {product.price || "—"}
                          </td>
                          <td className="text-center">
                            {product.supplier?.supplierName || "—"}
                          </td>
                          <td className="text-center">
                            <div className="form-check form-switch d-flex justify-content-center">
                              <Switch
                                className="d-block"
                                checked={!!product.status}
                                onChange={checked =>
                                  toggleStatus(product.id, checked)
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <div className="form-check form-switch d-flex justify-content-center">
                              <Switch
                                className="d-block"
                                checked={!!product.isChargeable}
                                onChange={checked =>
                                  toggleChargable(product.id, checked)
                                }
                              />
                            </div>
                          </td>
                          <td className="text-center">
                            {product.createdAt
                              ? moment(product.createdAt).format(
                                  "DD-MM-YYYY | hh:mm A"
                                )
                              : "—"}
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              color="info"
                              className="me-2"
                              onClick={() => openModal("view", product)}
                            >
                              <FaEye />
                            </Button>
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => openModal("edit", product)}
                            >
                              <FaPen />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* Pagination */}
                {totalItems > 0 && (
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                    <div className="text-muted">
                      Showing {currentProducts.length} of {totalItems} products
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        <FaChevronCircleLeft />
                      </Button>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          color={currentPage === i + 1 ? "primary" : "light"}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}

                      <Button
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        <FaChevronCircleRight />
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ProductModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        productData={productData}
        modalType={modalType}
        onSave={handleSave}
      />
    </div>
  )
}

export default Product
